import React, { FC } from 'react';
import { Field } from 'react-final-form';
import { DropdownOption, TravellerFields } from '../../../../types';
import { useTranslation } from 'react-i18next';
import Dropdown from '../../../../../BaseComponents/Dropdown';
import { Props as DropdownProps } from '../../../../../BaseComponents/Dropdown/components/Dropdown';
import { DirectionField } from '../../types';

interface Props extends Omit<DropdownProps, 'filterOptions'> {
	field: DirectionField;
	addOption: (field: TravellerFields, value: DropdownOption) => void;
	deleteOption: (field: TravellerFields, value: DropdownOption) => void;
	filterOptions: (options: DropdownOption[], value: string, selectedOptions: DropdownOption[]) => DropdownOption[];
}

const DropdownDirections: FC<Props> = ({ field, options, addOption, deleteOption, filterOptions, ...otherProps }) => {
	const { t } = useTranslation('Traveller');

	const { InputProps: inputProps, ...dropdownProps } = otherProps;

	return (
		<Field
			name={field.queryFieldName}
			render={({ input, meta }) => {
				const resetInput = () => input.onChange('');

				const onChange = (_: React.ChangeEvent<{}>, value: string | DropdownOption) => {
					if (typeof value !== 'string') {
						addOption(field.arrayFieldName, value);
					}
					resetInput();
				};

				return (
					<Dropdown
						value={null}
						options={options}
						onChange={onChange}
						onOpen={resetInput}
						onClose={resetInput}
						blurOnSelect={false}
						disableCloseOnSelect
						disableClearable
						disablePortal
						disabled={options?.length === field.selectedOptions?.length}
						filterOptions={options =>
							filterOptions(
								options.filter((opt): opt is DropdownOption => typeof opt !== 'string'),
								input.value,
								field.selectedOptions
							)
						}
						isOptionEqualToValue={(opt, value) =>
							typeof opt !== 'string' && typeof value !== 'string' && opt.value === value.value
						}
						noOptionsText={
							field.selectedAll
								? t('You have selected all available cities')
								: t('City not found. Try a different query')
						}
						InputProps={{
							...input,
							...inputProps,
							label: field.label,
							error: meta.error && meta.touched,
							helperText:
								inputProps.helperText && field.selectedAll
									? inputProps.helperText
									: meta.touched && t(meta.error)
						}}
						{...dropdownProps}
					/>
				);
			}}
		/>
	);
};

export default DropdownDirections;
